import { z } from "zod";

import { FILE_TYPES } from "@/lib/constants";

const IMSI_LENGTH = 15;
const OTP_LENGTH = 6;
const MAX_FILE_SIZE = 10_485_760;

export const submitOrderSchema = z
  .object({
    isAuthenticated: z.boolean(),
    customerType: z.enum([
      "RES",
      "MOBI-POSTPAID",
      "FREEDOM",
      "MOBI-PREPAID",
      "TOURIST",
    ]),
    icFront: z.instanceof(File).optional(),
    icBack: z.instanceof(File).optional(),
    subscriptionForm: z.instanceof(File).optional(),
    otherFile1: z.instanceof(File).optional(),
    otherFile2: z.instanceof(File).optional(),
    otherFile3: z.instanceof(File).optional(),
    imsiNumber: z.string().optional(),
    otp: z
      .string()
      .min(OTP_LENGTH, { message: "OTP must be 6 digits" })
      .max(OTP_LENGTH, { message: "OTP must be 6 digits" })
      .optional(),
    hasAgreedToTerms: z.literal<boolean>(true, {
      errorMap: () => ({
        message: "Please read and accept our terms and conditions",
      }),
    }),
  })
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Part of validation
  .superRefine((data, context) => {
    if (data.customerType !== "TOURIST") {
      if (data.icFront) {
        // @ts-expect-error - file.type is a string
        if (!FILE_TYPES.includes(data.icFront.type as string)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid file type",
            path: ["icFront"],
          });
        }

        if (data.icFront.size > MAX_FILE_SIZE) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "File size must be less than 10MB",
            path: ["icFront"],
          });
        }
      } else {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "IC Front is required",
          path: ["icFront"],
        });
      }

      if (data.icBack) {
        // @ts-expect-error - file.type is a string
        if (!FILE_TYPES.includes(data.icBack.type as string)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid file type",
            path: ["icBack"],
          });
        }

        if (data.icBack.size > MAX_FILE_SIZE) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "File size must be less than 10MB",
            path: ["icBack"],
          });
        }
      } else {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "IC Back is required",
          path: ["icBack"],
        });
      }

      if (data.isAuthenticated) {
        if (data.subscriptionForm) {
          // @ts-expect-error - file.type is a string
          if (!FILE_TYPES.includes(data.subscriptionForm.type as string)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid file type",
              path: ["subscriptionForm"],
            });
          }

          if (data.subscriptionForm.size > MAX_FILE_SIZE) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "File size must be less than 10MB",
              path: ["subscriptionForm"],
            });
          }
        } else {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Subscription Form is required",
            path: ["subscriptionForm"],
          });
        }

        if (data.otherFile1) {
          // @ts-expect-error - file.type is a string
          if (!FILE_TYPES.includes(data.otherFile1.type as string)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid file type",
              path: ["otherFile1"],
            });
          }

          if (data.otherFile1.size > MAX_FILE_SIZE) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "File size must be less than 10MB",
              path: ["otherFile1"],
            });
          }
        }

        if (data.otherFile2) {
          // @ts-expect-error - file.type is a string
          if (!FILE_TYPES.includes(data.otherFile2.type as string)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid file type",
              path: ["otherFile2"],
            });
          }

          if (data.otherFile2.size > MAX_FILE_SIZE) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "File size must be less than 10MB",
              path: ["otherFile2"],
            });
          }
        }

        if (data.otherFile3) {
          // @ts-expect-error - file.type is a string
          if (!FILE_TYPES.includes(data.otherFile3.type as string)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid file type",
              path: ["otherFile3"],
            });
          }

          if (data.otherFile3.size > MAX_FILE_SIZE) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "File size must be less than 10MB",
              path: ["otherFile3"],
            });
          }
        }

        if (data.customerType !== "RES") {
          if (data.imsiNumber) {
            if (data.imsiNumber.length !== IMSI_LENGTH) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `IMSI Number must be ${IMSI_LENGTH} digits`,
                path: ["imsiNumber"],
              });
            }
          } else {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: "IMSI Number is required",
              path: ["imsiNumber"],
            });
          }
        }
      }

      if (!data.otp) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "OTP is required",
          path: ["otp"],
        });
      }
    }
  });
